import React, { useEffect, useRef } from "react";
import {
  UnauthenticatedTemplate,
  AuthenticatedTemplate,
} from "@azure/msal-react";
import { BrowserRouter } from "react-router-dom";
import { useAuth } from "us.helper/hooks/useAuth";
import { Loader, LoginRedirect } from "us.common/components";
import {
  ConfigurationProvider,
  ConfigurationContext,
} from "us.common/ConfigurationContext";
import { Unauthorized } from "us.common/components";
import { FeatureFlagProvider } from "us.common/FeatureFlagContext";
import { SignalRProvider } from "us.common/SignalRProvider";
import { DrawerProvider } from "us.common/context";

/**
 * AuthGate centralizes authentication + (optionally) configuration readiness gating.
 * It prevents UI flicker by only deciding between authenticated vs unauthenticated
 * once MSAL initialization has completed.
 */
export type AuthReadyContext = { account: any; authResult: any };
export type AuthGateProps = {
  /** Whether downstream needs configuration/authorization profile before rendering */
  requireConfig?: boolean;
  /** Optional custom loader element */
  loaderFallback?: React.ReactNode;
  /** Children to render once authenticated (and config ready if required) */
  children: React.ReactNode;
  /** Callback when authenticated state becomes stable */
  onAuthReady?: (ctx: AuthReadyContext) => void;
  /** Callback when determined unauthenticated */
  onUnauthenticated?: (reason: string) => void;
};

export const AuthGate: React.FC<AuthGateProps> = ({
  children,
  loaderFallback,
  onAuthReady,
  onUnauthenticated,
  requireConfig = true,
}) => {
  const { isAuthenticated, isInitialized, authResult, account } = useAuth();
  const authReadyFired = useRef(false);

  // Fire ready callback exactly once when stable authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated && !authReadyFired.current) {
      authReadyFired.current = true;
      onAuthReady?.({ account, authResult });
    }
  }, [isInitialized, isAuthenticated, account, authResult, onAuthReady]);

  if (!isInitialized) {
    return <>{loaderFallback || <Loader />}</>;
  }

  if (!isAuthenticated) {
    onUnauthenticated?.("no-active-session");
    return (
      <UnauthenticatedTemplate>
        <BrowserRouter
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          }}
        >
          <LoginRedirect />
        </BrowserRouter>
      </UnauthenticatedTemplate>
    );
  }

  // Wrap in providers then gate on configuration context values
  return (
    <AuthenticatedTemplate>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <ConfigurationProvider>
          <ConfigurationContext.Consumer>
            {(ctx) => {
              const { configurationDetail, isConfigLoading } = ctx;
              const configReady = configurationDetail?.length > 0;
              if (requireConfig && (isConfigLoading || !configReady)) {
                return loaderFallback || <Loader />;
              }
              if (requireConfig && !isConfigLoading && !configReady) {
                return <Unauthorized />;
              }
              return (
                <FeatureFlagProvider>
                  <SignalRProvider>
                    <DrawerProvider>{children}</DrawerProvider>
                  </SignalRProvider>
                </FeatureFlagProvider>
              );
            }}
          </ConfigurationContext.Consumer>
        </ConfigurationProvider>
      </BrowserRouter>
    </AuthenticatedTemplate>
  );
};

export default AuthGate;
